import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { provisionTenant } from "../auth/api";
import { useAuth } from "../auth/AuthProvider";

function slugify(raw: string) {
  const s = (raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  return (s || "tenant").slice(0, 64);
}

export default function ProvisionTenantPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh, signOut, state } = useAuth();

  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const suggestedSlug = useMemo(() => slugify(tenantName), [tenantName]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div
        style={{
          width: "min(720px, 100%)",
          borderRadius: 16,
          border: "1px solid rgba(0,0,0,0.10)",
          background: "white",
          padding: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 950 }}>Create your tenant</div>
        <div style={{ marginTop: 6, color: "rgba(0,0,0,0.65)", fontSize: 13, lineHeight: 1.5 }}>
          You’re authenticated, but you don’t belong to a tenant yet. Create one to continue.
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 10, justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
            Token present: <code>{state.token ? "yes" : "no"}</code>
          </div>

          <button
            type="button"
            onClick={() => {
              signOut();
              nav("/login", { replace: true });
            }}
            style={{
              padding: "8px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.12)",
              background: "rgba(0,0,0,0.02)",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Sign out
          </button>
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy) return;

            const name = tenantName.trim();
            if (!name) {
              setErr("Tenant name is required.");
              return;
            }

            const slugInput = (tenantSlug || "").trim();
            const slug = slugInput ? slugify(slugInput) : suggestedSlug;

            setBusy(true);
            setErr(null);

            try {
              await provisionTenant({ tenant_name: name, tenant_slug: slug });

              // Refresh identity state. /v1/auth/me should now return roles+tenant.
              await refresh();
              
              // Land in admin, trigger first-time welcome modal (OverviewPage listens for ?first_time=true)
              // If a next=... param exists, preserve it and append first_time=true to that target.
              const sp = new URLSearchParams(loc.search || "");
              const next = sp.get("next") || "/admin/overview";

              let dest = next;
              try {
                const u = new URL(next, window.location.origin);
                if (u.searchParams.get("first_time") !== "true") {
                  u.searchParams.set("first_time", "true");
                }
                dest = u.pathname + u.search;
              } catch {
                // fallback for non-URL strings
                dest = next.includes("?")
                  ? `${next}&first_time=true`
                  : `${next}?first_time=true`;
              }

              nav(dest, { replace: true });
              
            } catch (e2: any) {
              const msg =
                e2?.message ||
                e2?.detail?.message ||
                e2?.detail ||
                "Provisioning failed. Please try again.";
              setErr(String(msg));
            } finally {
              setBusy(false);
            }
          }}
          style={{ marginTop: 16, display: "grid", gap: 12 }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>Tenant name</div>
            <input
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              placeholder="Acme Inc"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                outline: "none",
                fontSize: 14,
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>Tenant slug (optional)</div>
            <input
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              placeholder={suggestedSlug}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                outline: "none",
                fontSize: 14,
              }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: "rgba(0,0,0,0.60)" }}>
              If left blank, we’ll use: <code>{suggestedSlug}</code>
            </div>
          </div>

          {err && (
            <div
              style={{
                padding: 12,
                borderRadius: 12,
                background: "rgba(255,0,0,0.08)",
                border: "1px solid rgba(176,0,32,0.18)",
                color: "#b00020",
                fontWeight: 700,
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            style={{
              padding: "10px 14px",
              fontWeight: 950,
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.10)",
              background: busy ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.90)",
              color: busy ? "rgba(0,0,0,0.55)" : "white",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Creating..." : "Create tenant"}
          </button>
        </form>
      </div>
    </div>
  );
}